import React from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import {
  useAuthentication,
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneSettings,
  useValidatePersonalTimeRecordsUpToDate,
} from "@planzone/view-models";
import { events } from "@planzone/events";
import { useMessage } from "@components/useMessage";
import { usePlanzonePermissions } from "@components/permissions";

import { Props } from "./types";
import { isValidated } from "./utils";
import { CheckMark } from "./checkMark";
import {
  parseAsLocalDate,
  parseTimestamp,
  toFixed,
  utcDt,
} from "@pages/sharedTimeSheetUtils";
import styles from "./styles.scss";
import { useTranslation } from "react-i18next";

function Total({
  dates,
  timeSheet,
  areDetailsDisplayed,
  resource,
  isTeam,
}: Props) {
  const { t } = useTranslation();
  const { isRequestPending, validatePersonalTimeRecordsUpToDate } =
    useValidatePersonalTimeRecordsUpToDate(resource);
  const [integrationDate, setIntegrationDate] = React.useState<DateTime | null>(
    !!resource.integratedAt ? parseTimestamp(resource.integratedAt) : null,
  );
  const hasIntegrationDate = integrationDate !== null;
  const [hoveredDate, setHoveredDate] = React.useState<DateTime | null>(null);
  const { account } = useAuthentication();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { isAdministrator } = usePlanzonePermissions();
  const { settings } = useRetrievePlanzoneSettings(planzone);
  const canEdit =
    isAdministrator ||
    (resource.uuid === account.resource.uuid &&
      settings.canApprovePersonalTimeSheet) ||
    settings.canApproveProjectPersonalTimeSheet ||
    settings.canApproveManagerPersonalTimeSheet;

  React.useEffect(() => {
    if (resource.integratedAt) {
      const nextDate = parseTimestamp(resource.integratedAt);
      if (integrationDate === null || !integrationDate.equals(nextDate)) {
        setIntegrationDate(nextDate);
      }
    }
  }, [resource.integratedAt]);

  useMessage(({ type, data }) => {
    if (settings.areTimeSheetsAutoIntegrated) {
      if (
        type === events.saveTimeRecordSucceeded &&
        data.timeRecord.resource.uuid === resource.uuid
      ) {
        const recordDate = utcDt(
          data.timeRecord.year,
          data.timeRecord.month - 1,
          data.timeRecord.date,
        );

        if (integrationDate === null || recordDate > integrationDate) {
          setIntegrationDate(recordDate);
        }
      }
    }
  });

  return (
    <div className={styles.total}>
      {timeSheet.spentTimes.map(({ date: d, time }) => {
        const date = parseAsLocalDate(d);
        const validated =
          hasIntegrationDate && isValidated(integrationDate, date);
        const pendingValidation = !!hoveredDate && date <= hoveredDate;
        const hovered = !!hoveredDate && date.equals(hoveredDate);

        return (
          <div
            key={date.toISO() ?? ""}
            className={classNames(styles.day, {
              [styles.validated]: validated,
              [styles.pendingValidation]: pendingValidation,
              [styles.hovered]: hovered,
            })}
            onMouseEnter={() =>
              canEdit &&
              !settings.areTimeSheetsAutoIntegrated &&
              setHoveredDate(date)
            }
            onMouseLeave={() =>
              canEdit &&
              !settings.areTimeSheetsAutoIntegrated &&
              setHoveredDate(null)
            }
          >
            <div className={styles.record}>
              {!settings.areTimeSheetsAutoIntegrated &&
                (planzone.uuid === "57ec7e80-dcbb-49af-b11f-665fa6e545c1"
                  ? isTeam
                  : true) && (
                  <div
                    className={styles.button}
                    onClick={() =>
                      canEdit &&
                      !isRequestPending &&
                      validatePersonalTimeRecordsUpToDate({
                        resource: resource,
                        date: date.toUTC().toISO() ?? "",
                      })
                    }
                  >
                    <CheckMark
                      className={styles.checkMark}
                      isChecked={
                        !!hoveredDate ? hoveredDate >= date : validated
                      }
                    />
                  </div>
                )}
              <p className={styles.text}>{toFixed(time)}</p>
            </div>
          </div>
        );
      })}
      {areDetailsDisplayed && (
        <div className={classNames(styles.day, styles.deadline)}>
          {timeSheet.timesheetDeadline && (
            <p className={styles.text}>
              {t("timeSheet.lockDate")} :{" "}
              {DateTime.fromISO(timeSheet.timesheetDeadline, { zone: "utc" })
                .toLocal()
                .toLocaleString(DateTime.DATE_SHORT)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export { Total };
